import React, { useState } from 'react';
import {
    LuActivity,
    LuClipboardList,
    LuFileType,
    LuLoader,
    LuPen,
    LuPlus,
    LuSquareCheck,
    LuTrendingUp,
    LuUser,
    LuX,
} from 'react-icons/lu';
import { Link, useSearchParams } from 'react-router-dom';

import { FeatureFlag } from '@ndvr/auth-api';
import { ActionItemView, TaskView } from '@ndvr/customer-api';

import { ClockIcon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Page } from '@/components/ui/page';
import { useAssignedFlags } from '@/views/auth/auth-queries';
import { formatDate, formatDateTime, stripHtmlToText, titleCase, useRequiredParams } from 'utils';
import { useHouseholdTeamNameResolver } from 'views/admin/tenantTeam/tenant-team-queries';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';
import { useGetMeetingBot } from 'views/household/HouseholdEvents/events-queries';
import { ApplyNoteTemplateDialog } from 'views/household/HouseholdNotes/ApplyNoteTemplateDialog';
import { getNotePersonName } from 'views/household/HouseholdNotes/note-utils';
import { useListTasksByNoteQuery } from 'views/tasks/task-queries';
import { TaskFormType, toActionItemFormValues, toTaskFormValues } from 'views/tasks/widgets/TaskForm';
import { PageSpinner } from 'widgets';

import { useDockState } from '../../dock/dock-state';
import { useAdvisorLogin, useGlobalAccount } from '../household-queries';
import { useActionItemsByNoteQuery, useDismissActionItemMutation } from './action-items-query';
import { NewHouseholdNotePage } from './NewHouseholdNotePage';
import {
    useApplyTemplateToNoteMutation,
    useCreatePortfolioFromTranscript,
    useMeetingTranscript,
    useNoteQuery,
} from './note-queries';
import { NoteBodyCard } from './NoteBodyCard';
import { NoteTranscriptTabs } from './NoteTranscriptTabs';

// CC_NEW_NOTE_PAGE swaps in the redesigned page.
export const HouseholdNotePage = () => {
    const flagsQuery = useAssignedFlags();
    const isEnabled = useFeatureFlag(FeatureFlag.CC_NEW_NOTE_PAGE);

    if (flagsQuery.isPending) return <PageSpinner />;
    return isEnabled ? <NewHouseholdNotePage /> : <LegacyHouseholdNotePage />;
};

const LegacyHouseholdNotePage = () => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const [searchParams] = useSearchParams();
    const noteGUID = searchParams.get('noteGUID') ?? '';
    const [isApplyNoteTemplateDialogOpen, setIsApplyNoteTemplateDialogOpen] = useState(false);

    const globalAccountQuery = useGlobalAccount(globalAccountGUID);
    const noteQuery = useNoteQuery(noteGUID);
    const actionItemsQuery = useActionItemsByNoteQuery(noteGUID);
    const dismissActionItemMutation = useDismissActionItemMutation(noteGUID);
    const [dismissingGUID, setDismissingGUID] = useState<string | null>(null);
    const noteTasksQuery = useListTasksByNoteQuery(noteGUID);
    const { addCreateTaskDockItem, addUpdateTaskDockItem } = useDockState();
    const { nameFor } = useHouseholdTeamNameResolver(globalAccountGUID);
    const createPortfolioMutation = useCreatePortfolioFromTranscript(globalAccountGUID, noteGUID);
    const advisorLoginMutation = useAdvisorLogin();
    const applyTemplateMutation = useApplyTemplateToNoteMutation(globalAccountGUID, noteGUID);

    const meetingBotQuery = useGetMeetingBot(
        globalAccountGUID,
        noteQuery.data?.eventGUID ?? '',
        !!noteQuery.data?.eventGUID,
    );
    const transcript = useMeetingTranscript(noteQuery.data?.meetingBotGUID);

    if (globalAccountQuery.isLoading || noteQuery.isLoading) {
        return <PageSpinner />;
    }

    if (!noteQuery.data || !globalAccountQuery.data) {
        return null;
    }

    const note = noteQuery.data;
    const personName = getNotePersonName(note.createdBy, nameFor);
    const updatedPersonName = note.updatedBy ? getNotePersonName(note.updatedBy, nameFor) : personName;
    const actionItems = (actionItemsQuery.data ?? []).filter((actionItem) => !actionItem.convertedTaskGUID);
    const associatedTasks = noteTasksQuery.data ?? [];
    const isMeetingBotProcessing = Boolean(note.meetingBotGUID && meetingBotQuery.data?.status === 'PROCESSING');

    const handleCreateTaskFromActionItem = (actionItem: ActionItemView) =>
        addCreateTaskDockItem(
            globalAccountGUID,
            toActionItemFormValues(actionItem) as TaskFormType,
            undefined,
            actionItem.actionItemGUID,
            noteGUID,
        );

    const handleEditTask = (task: TaskView) => {
        const formValues = toTaskFormValues(task) as TaskFormType;
        addUpdateTaskDockItem(task.taskGUID, task.householdGUID ?? undefined, formValues, '', 'edit');
    };

    return (
        <Page>
            <main className="flex flex-col gap-4 overflow-hidden p-6 pb-8">
                <div className="flex items-center justify-between gap-4">
                    <h1 className="flex-1 truncate text-2xl font-bold">{note.subject || 'Untitled Note'}</h1>
                    <div className="flex items-center gap-2">
                        {note.meetingBotGUID && (
                            <Button
                                onClick={() => setIsApplyNoteTemplateDialogOpen(true)}
                                disabled={applyTemplateMutation.isPending}
                                variant="outline"
                                className="flex items-center gap-2"
                            >
                                {applyTemplateMutation.isPending ? (
                                    <LuLoader className="h-4 w-4 animate-spin" />
                                ) : (
                                    <LuFileType className="h-4 w-4" />
                                )}
                                <span>Apply Note Template</span>
                            </Button>
                        )}
                        {note.meetingBotGUID && !note.accountGroupGUID && (
                            <Button
                                onClick={() => createPortfolioMutation.mutate(note.meetingBotGUID || '')}
                                disabled={createPortfolioMutation.isPending}
                                variant="outline"
                                className="flex items-center gap-2"
                            >
                                {createPortfolioMutation.isPending ? (
                                    <LuLoader className="h-4 w-4 animate-spin" />
                                ) : (
                                    <LuTrendingUp className="h-4 w-4" />
                                )}
                                <span>Convert to Financial Plan</span>
                            </Button>
                        )}
                        {note.accountGroupGUID && (
                            <Button
                                onClick={() =>
                                    advisorLoginMutation.mutate({
                                        householdGUID: globalAccountGUID,
                                        redirect: `/lab/${note.accountGroupGUID}`,
                                    })
                                }
                                disabled={advisorLoginMutation.isPending}
                                variant="outline"
                                className="flex items-center gap-2"
                            >
                                <LuActivity className="h-4 w-4" />
                                <span>Analyze in Portfolio Lab&#8482;</span>
                            </Button>
                        )}
                        <Button asChild variant="outline">
                            <Link to={`../notes/edit?noteGUID=${noteGUID}`} className="flex items-center">
                                <LuPen /> <span>Edit Note</span>
                            </Link>
                        </Button>
                    </div>
                </div>

                {/* Two-column layout: Note content on left, Action Items & Tasks on right */}
                <div className="flex flex-1 gap-4 overflow-hidden">
                    {/* Left column: Note content */}
                    <div className="flex w-0 flex-1 flex-col gap-4 overflow-y-auto">
                        <NoteTranscriptTabs transcript={transcript}>
                            <NoteBodyCard note={note} personName={personName} updatedPersonName={updatedPersonName} />
                        </NoteTranscriptTabs>
                    </div>

                    {/* Right column: Action Items & Tasks */}
                    <div className="flex w-[480px] flex-shrink-0 flex-col gap-4 overflow-y-auto">
                        <Card className="bg-white">
                            <CardContent className="flex flex-col gap-4 p-6">
                                <div className="flex flex-col gap-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                            <LuSquareCheck className="h-5 w-5 text-slate-600" />
                                            <h2 className="text-lg font-semibold text-slate-900">Associated Tasks</h2>
                                        </div>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            className="gap-2"
                                            onClick={() =>
                                                addCreateTaskDockItem(
                                                    globalAccountGUID,
                                                    undefined,
                                                    undefined,
                                                    undefined,
                                                    noteGUID,
                                                )
                                            }
                                        >
                                            <LuPlus className="h-4 w-4" />
                                            <span>Add Task</span>
                                        </Button>
                                    </div>
                                    <p className="text-sm text-slate-500">Tasks created from or linked to this note.</p>
                                </div>

                                <div className="flex flex-col gap-3">
                                    {noteTasksQuery.isLoading && (
                                        <div className="flex items-center gap-2 text-sm text-slate-500">
                                            <LuLoader className="h-4 w-4 animate-spin" />
                                            <span>Loading associated tasks…</span>
                                        </div>
                                    )}

                                    {noteTasksQuery.isError && !noteTasksQuery.isLoading && (
                                        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                                            Unable to load associated tasks right now. Please try again later.
                                        </div>
                                    )}

                                    {!noteTasksQuery.isLoading &&
                                        !noteTasksQuery.isError &&
                                        associatedTasks.length === 0 && (
                                            <div className="rounded-md border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500">
                                                No tasks associated with this note.
                                            </div>
                                        )}

                                    {!noteTasksQuery.isLoading &&
                                        !noteTasksQuery.isError &&
                                        associatedTasks.map((task) => {
                                            const assigneeName = task.assignee
                                                ? (task.assigneeName ?? getNotePersonName(task.assignee, nameFor))
                                                : null;
                                            const dueDateLabel = task.dueDate
                                                ? formatDate(task.dueDate, 'MM/dd/yyyy')
                                                : null;

                                            return (
                                                <div
                                                    key={task.taskGUID}
                                                    className="cursor-pointer rounded-lg border border-slate-200 bg-slate-50 p-4 shadow-sm transition hover:border-slate-300"
                                                    onClick={() => handleEditTask(task)}
                                                    role="button"
                                                    tabIndex={0}
                                                    onKeyDown={(event) => {
                                                        if (event.key === 'Enter' || event.key === ' ') {
                                                            event.preventDefault();
                                                            handleEditTask(task);
                                                        }
                                                    }}
                                                >
                                                    <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                                                        <div className="flex-1">
                                                            <p className="font-medium text-slate-900">
                                                                {task.title ?? 'Untitled task'}
                                                            </p>
                                                            {task.description && (
                                                                <p className="text-sm text-slate-600">
                                                                    {stripHtmlToText(task.description)}
                                                                </p>
                                                            )}
                                                            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                                                                <span className="rounded-full bg-slate-200 px-2 py-0.5 font-medium text-slate-700">
                                                                    {titleCase(task.status)}
                                                                </span>
                                                                {assigneeName && (
                                                                    <span className="flex items-center gap-1">
                                                                        <LuUser className="h-3.5 w-3.5" />
                                                                        <span>{assigneeName}</span>
                                                                    </span>
                                                                )}
                                                                {dueDateLabel && (
                                                                    <span className="flex items-center gap-1">
                                                                        <ClockIcon className="h-3.5 w-3.5" />
                                                                        <span>Due {dueDateLabel}</span>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            </CardContent>
                        </Card>

                        {note.meetingBotGUID && (
                            <Card className="border-blue-100 bg-blue-50">
                                <CardContent className="flex flex-col gap-4 p-6">
                                    <div className="flex flex-col gap-2">
                                        <div className="flex items-center gap-2 text-blue-700">
                                            <LuClipboardList className="h-5 w-5" />
                                            <h2 className="text-lg font-semibold">AI-Suggested Action Items</h2>
                                        </div>
                                        <p className="text-sm text-blue-700">
                                            Automatically extracted from your meeting notes
                                        </p>
                                    </div>

                                    <div className="flex flex-col gap-3">
                                        {(actionItemsQuery.isLoading || isMeetingBotProcessing) && (
                                            <div className="flex items-center gap-2 text-sm text-blue-700">
                                                <LuLoader className="h-4 w-4 animate-spin" />
                                                <span>
                                                    {isMeetingBotProcessing
                                                        ? 'Processing meeting transcript and extracting action items…'
                                                        : 'Loading action items…'}
                                                </span>
                                            </div>
                                        )}

                                        {actionItemsQuery.isError &&
                                            !actionItemsQuery.isLoading &&
                                            !isMeetingBotProcessing && (
                                                <div className="rounded-md border border-blue-200 bg-white p-3 text-sm text-blue-700">
                                                    Unable to load action items right now. Please try again later.
                                                </div>
                                            )}

                                        {!actionItemsQuery.isLoading &&
                                            !actionItemsQuery.isError &&
                                            !isMeetingBotProcessing &&
                                            actionItems.length === 0 && (
                                                <div className="rounded-md border border-blue-200 bg-white p-3 text-sm text-blue-700">
                                                    No action items were identified for this note.
                                                </div>
                                            )}

                                        {!actionItemsQuery.isLoading &&
                                            !actionItemsQuery.isError &&
                                            !isMeetingBotProcessing &&
                                            actionItems.map((actionItem) => {
                                                const assigneeName = actionItem.assignee
                                                    ? getNotePersonName(actionItem.assignee, nameFor)
                                                    : null;

                                                return (
                                                    <div
                                                        key={actionItem.actionItemGUID}
                                                        className="flex flex-col gap-3 rounded-lg bg-white p-4 shadow-sm"
                                                    >
                                                        <div className="relative">
                                                            <button
                                                                type="button"
                                                                className="absolute -top-1 -right-1 rounded-full p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-50"
                                                                disabled={dismissingGUID === actionItem.actionItemGUID}
                                                                onClick={() => {
                                                                    setDismissingGUID(actionItem.actionItemGUID);
                                                                    dismissActionItemMutation.mutate(
                                                                        actionItem.actionItemGUID,
                                                                        {
                                                                            onSettled: () => setDismissingGUID(null),
                                                                        },
                                                                    );
                                                                }}
                                                                aria-label="Dismiss action item"
                                                            >
                                                                {dismissingGUID === actionItem.actionItemGUID ? (
                                                                    <LuLoader className="h-4 w-4 animate-spin" />
                                                                ) : (
                                                                    <LuX className="h-4 w-4" />
                                                                )}
                                                            </button>
                                                            <p className="font-medium text-slate-900">
                                                                {actionItem.title ?? 'Untitled action item'}
                                                            </p>
                                                            {actionItem.description && (
                                                                <p className="mt-1 text-sm text-slate-600">
                                                                    {actionItem.description}
                                                                </p>
                                                            )}
                                                            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                                                                {assigneeName && (
                                                                    <span className="flex items-center gap-1">
                                                                        <LuUser className="h-3.5 w-3.5" />
                                                                        <span>Assigned to {assigneeName}</span>
                                                                    </span>
                                                                )}
                                                                {actionItem.dueDate && (
                                                                    <span className="flex items-center gap-1">
                                                                        <ClockIcon className="h-3.5 w-3.5" />
                                                                        <span>
                                                                            Due {formatDateTime(actionItem.dueDate)}
                                                                        </span>
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="w-full gap-2"
                                                            onClick={() => handleCreateTaskFromActionItem(actionItem)}
                                                        >
                                                            <LuPlus className="h-4 w-4" />
                                                            <span>Create Task</span>
                                                        </Button>
                                                    </div>
                                                );
                                            })}
                                    </div>

                                    <div className="flex items-start gap-2 rounded-md bg-blue-100 p-3 text-xs text-blue-700">
                                        <ClockIcon className="h-4 w-4 flex-shrink-0" />
                                        <span>
                                            These action items were automatically identified from your meeting
                                            transcript. Review and convert relevant items to tasks.
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </main>

            <ApplyNoteTemplateDialog
                globalAccountGUID={globalAccountGUID}
                noteGUID={note.noteGUID}
                isOpen={isApplyNoteTemplateDialogOpen}
                setIsOpen={setIsApplyNoteTemplateDialogOpen}
            />
        </Page>
    );
};
